import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { HTTPError } from 'ky';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { apiClient } from '@/shared/api/client';
import { useSessionStore } from '@/entities/session';
import { needsEmailVerificationFromProfile } from '@/shared/lib/emailVerification';

// Soft-mode email verification.
//
// In edu-balance the user is NOT bounced to /verify-email after login. Instead a
// non-blocking banner is shown across the app (see widgets/EmailVerifyBanner).
// This guard now only enforces verification on a small list of "critical" routes,
// so users can keep browsing courses, completing profile, etc. while their
// 7-day grace period is active. The list mirrors the backend `RequireEmailVerified`
// middleware in `pkg/middleware/email_verified.go`.
const CRITICAL_PREFIXES = [
  '/app/billing/checkout',
  '/app/settings/security',
  '/app/settings/email',
  '/app/courses', // certificate issuance is here once implemented
];

const VERIFY_REQUIRED_PATH = '/app/verify-required';

function isCriticalRoute(pathname: string): boolean {
  return CRITICAL_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function EmailVerificationGuard() {
  const location = useLocation();
  const logout = useSessionStore((s) => s.logout);
  const [loading, setLoading] = useState(true);
  const [needsVerify, setNeedsVerify] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await apiClient.get('profile/me').json<{
          email?: string;
          emailVerified?: boolean;
          email_verified?: boolean;
        }>();
        if (cancelled) return;
        setNeedsVerify(needsEmailVerificationFromProfile(p));
      } catch (e) {
        if (cancelled) return;
        if (e instanceof HTTPError && e.response.status === 401) {
          logout();
          return;
        }
        setNeedsVerify(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, logout]);

  const blocked = useMemo(
    () => needsVerify && isCriticalRoute(location.pathname),
    [needsVerify, location.pathname],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <PageSpinner />
      </div>
    );
  }

  if (blocked && location.pathname !== VERIFY_REQUIRED_PATH) {
    return <Navigate to={VERIFY_REQUIRED_PATH} replace state={{ from: location }} />;
  }

  if (!needsVerify && location.pathname === VERIFY_REQUIRED_PATH) {
    return <Navigate to="/app" replace />;
  }

  return <Outlet />;
}
